(() => {
  const carouselSelector = '[data-carousel]';
  const trackSelector = '[data-carousel-track]';
  const prevSelector = '[data-carousel-prev]';
  const nextSelector = '[data-carousel-next]';
  const responsiveImageSelector = 'img[data-carousel-lazy-srcset], img[srcset]';
  const initializedAttribute = 'data-carousel-initialized';
  const shuffledAttribute = 'data-carousel-shuffled';
  const postPaintReadyAttribute = 'data-carousel-post-paint-ready';
  const postPaintReadyEvent = 'carousel:post-paint-ready';
  const scrollTolerance = 8;
  const coverScaleAllowance = 1.04;
  const imageLoads = new WeakMap();
  const pendingCarousels = new Set();
  let cascadeStarted = false;
  let cascadeRunning = false;

  const seededRandom = (seed) => {
    let value = seed % 2147483647;

    if (value <= 0) value += 2147483646;

    return () => {
      value = (value * 16807) % 2147483647;
      return (value - 1) / 2147483646;
    };
  };

  const shuffleTrack = (track) => {
    if (!track.hasAttribute('data-carousel-shuffle') || track.hasAttribute(shuffledAttribute)) return;

    const items = Array.from(track.children);
    const random = seededRandom(Date.now());

    for (let index = items.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(random() * (index + 1));
      [items[index], items[swapIndex]] = [items[swapIndex], items[index]];
    }

    items.forEach((item) => track.appendChild(item));
    track.setAttribute(shuffledAttribute, 'true');
  };

  const updateResponsiveImageSize = (image) => {
    if (!(image instanceof HTMLImageElement)) return;

    const sourceWidth = Number.parseFloat(image.getAttribute('width') || '');
    const sourceHeight = Number.parseFloat(image.getAttribute('height') || '');
    const renderedWidth = image.clientWidth;
    const renderedHeight = image.clientHeight;

    if (
      ![sourceWidth, sourceHeight, renderedWidth, renderedHeight].every(Number.isFinite) ||
      sourceWidth <= 0 ||
      sourceHeight <= 0 ||
      renderedWidth <= 0 ||
      renderedHeight <= 0
    ) {
      return;
    }

    const computedStyle = window.getComputedStyle(image);

    if (computedStyle.filter?.includes('blur(')) return;

    const sourceAspectRatio = sourceWidth / sourceHeight;
    let paintedWidth = renderedWidth;
    if (computedStyle.objectFit === 'cover') paintedWidth = Math.max(renderedWidth, renderedHeight * sourceAspectRatio);
    if (computedStyle.objectFit === 'contain') paintedWidth = Math.min(renderedWidth, renderedHeight * sourceAspectRatio);
    const hoverScale = image.matches('.featured-products-carousel__image') ? coverScaleAllowance : 1;
    // sizes is in CSS pixels; srcset selection applies the screen's pixel density.
    const responsiveSize = `${Math.ceil(paintedWidth * hoverScale)}px`;

    if (image.sizes !== responsiveSize) {
      image.sizes = responsiveSize;
    }
  };

  const updateResponsiveImageSizes = (scope) => {
    scope.querySelectorAll(responsiveImageSelector).forEach(updateResponsiveImageSize);
  };

  const hydrateLazyImage = (image, { eager = false } = {}) => {
    if (!image || !image.hasAttribute('data-carousel-lazy-image')) return;

    const lazySrc = image.getAttribute('data-carousel-lazy-src');
    const lazySrcset = image.getAttribute('data-carousel-lazy-srcset');

    updateResponsiveImageSize(image);

    if (eager) {
      image.setAttribute('loading', 'eager');
      image.setAttribute('fetchpriority', 'low');
    }

    if (lazySrcset) {
      image.setAttribute('srcset', lazySrcset);
      image.removeAttribute('data-carousel-lazy-srcset');
    }

    if (lazySrc) {
      image.setAttribute('src', lazySrc);
      image.removeAttribute('data-carousel-lazy-src');
    }

    image.removeAttribute('data-carousel-lazy-image');
  };

  const loadImage = (image) => {
    updateResponsiveImageSize(image);
    if (imageLoads.has(image)) return imageLoads.get(image).promise;

    let finish;
    const promise = new Promise((resolve) => {
      finish = () => {
        image.removeEventListener('load', finish);
        image.removeEventListener('error', finish);
        imageLoads.delete(image);
        resolve();
      };
      image.addEventListener('load', finish);
      image.addEventListener('error', finish);
    });
    imageLoads.set(image, { promise, finish });
    image.loading = 'eager';
    if (image.getAttribute('fetchpriority') !== 'high') image.setAttribute('fetchpriority', 'low');
    hydrateLazyImage(image, { eager: true });
    if (image.complete) finish();
    return promise;
  };

  const loadCarousel = (carousel) => {
    const track = carousel.querySelector(trackSelector);
    if (!track) return Promise.resolve();
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return Promise.resolve();
    const rtl = window.getComputedStyle(track).direction === 'rtl';
    const left = rect.left - (rtl ? rect.width : 0);
    const right = rect.right + (rtl ? 0 : rect.width);
    // Load the current page and one page ahead, not the entire horizontal shelf.
    const images = Array.from(track.children).filter((item) => {
      const itemRect = item.getBoundingClientRect();
      return itemRect.right > left && itemRect.left < right;
    }).flatMap((item) => Array.from(item.querySelectorAll('img')));
    return Promise.all(images.map(loadImage));
  };

  const runCascade = async () => {
    if (!cascadeStarted || cascadeRunning) return;
    cascadeRunning = true;
    try {
      while (pendingCarousels.size) {
        // Re-read DOM order so editor moves and newly inserted sections join correctly.
        const next = carouselElements().find((carousel) => pendingCarousels.has(carousel));
        if (!next) {
          pendingCarousels.clear();
          break;
        }
        pendingCarousels.delete(next);
        if (next.closest('[hidden]')) continue;
        await loadCarousel(next);
      }
    } finally {
      cascadeRunning = false;
    }
  };

  const startCascade = () => {
    cascadeStarted = true;
    window.removeEventListener('scroll', startCascade);
    runCascade();
  };

  const hydrateInitialCarouselFrames = (scope = document) => {
    carouselElements(scope).forEach((carousel) => {
      const rect = carousel.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) loadCarousel(carousel);
      pendingCarousels.add(carousel);
    });
    if (window.scrollY > 0) startCascade();
    else runCascade();
  };

  const scheduleInitialCarouselFrameHydration = (scope = document) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        hydrateInitialCarouselFrames(scope);

        if (!document.documentElement.hasAttribute(postPaintReadyAttribute)) {
          document.documentElement.setAttribute(postPaintReadyAttribute, 'true');
          document.dispatchEvent(new CustomEvent(postPaintReadyEvent));
        }
      });
    });
  };

  const carouselElements = (scope = document) => {
    const scopedCarousels = Array.from(scope.querySelectorAll(carouselSelector));

    if (scope.matches && scope.matches(carouselSelector)) {
      scopedCarousels.unshift(scope);
    }

    return scopedCarousels.filter((element) => !element.closest('[data-grid-insert-pool]'));
  };

  class Carousel {
    constructor(element) {
      this.element = element;
      this.track = element.querySelector(trackSelector);
      this.prevButton = element.querySelector(prevSelector);
      this.nextButton = element.querySelector(nextSelector);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!this.track || !this.prevButton || !this.nextButton) return;

      shuffleTrack(this.track);

      this.updateFrame = null;
      this.needsImageSizeUpdate = true;
      this.loadForInteraction = () => loadCarousel(this.element);
      this.onScroll = () => {
        this.loadForInteraction();
        this.requestUpdate();
      };
      this.onResize = () => {
        this.needsImageSizeUpdate = true;
        this.requestUpdate();
        hydrateInitialCarouselFrames(this.element);
      };
      this.onPrevClick = () => this.scrollByPage(-1);
      this.onNextClick = () => this.scrollByPage(1);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.prevButton.addEventListener('click', this.onPrevClick);
      this.nextButton.addEventListener('click', this.onNextClick);
      this.element.addEventListener('pointerdown', this.loadForInteraction);
      this.element.addEventListener('focusin', this.loadForInteraction);
      window.addEventListener('resize', this.onResize);

      this.update();
    }

    destroy() {
      if (this.updateFrame) window.cancelAnimationFrame(this.updateFrame);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.prevButton?.removeEventListener('click', this.onPrevClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);
      this.element.removeEventListener('pointerdown', this.loadForInteraction);
      this.element.removeEventListener('focusin', this.loadForInteraction);
      window.removeEventListener('resize', this.onResize);
      pendingCarousels.delete(this.element);
      this.track?.querySelectorAll('img').forEach((image) => imageLoads.get(image)?.finish());
    }

    scrollByPage(direction) {
      this.loadForInteraction();
      const scrollAmount = this.track.clientWidth * 0.85 * direction;

      this.track.scrollBy({
        left: scrollAmount,
        behavior: this.reducedMotion ? 'auto' : 'smooth'
      });
    }

    requestUpdate() {
      if (this.updateFrame) return;

      this.updateFrame = window.requestAnimationFrame(() => {
        this.updateFrame = null;
        this.update();
      });
    }

    update() {
      if (this.needsImageSizeUpdate) {
        updateResponsiveImageSizes(this.track);
        this.needsImageSizeUpdate = false;
      }

      const maxScrollLeft = this.track.scrollWidth - this.track.clientWidth;
      const visibleItems = Number.parseInt(this.element.dataset.carouselVisibleItems || '0', 10);
      const hasVisibleItemLimit =
        Number.isFinite(visibleItems) &&
        visibleItems > 0 &&
        this.track.children.length <= visibleItems &&
        window.matchMedia('(min-width: 990px)').matches;
      const hasOverflow = !hasVisibleItemLimit && maxScrollLeft > scrollTolerance;
      const scrollLeft = Math.ceil(this.track.scrollLeft);
      const hidePrev = !hasOverflow || scrollLeft <= scrollTolerance;
      const hideNext = !hasOverflow || scrollLeft >= maxScrollLeft - scrollTolerance;

      if (this.prevButton.hidden !== hidePrev) {
        this.prevButton.hidden = hidePrev;
      }

      if (this.nextButton.hidden !== hideNext) {
        this.nextButton.hidden = hideNext;
      }
    }
  }

  const initializeCarousels = (scope = document) => {
    carouselElements(scope).forEach((element) => {
      if (element.hasAttribute(initializedAttribute)) return;

      element.setAttribute(initializedAttribute, 'true');
      element.carouselInstance = new Carousel(element);
    });

    scheduleInitialCarouselFrameHydration(scope);
  };

  const refreshCarousels = (scope = document) => {
    carouselElements(scope).forEach((element) => {
      if (element.carouselInstance) {
        element.carouselInstance.needsImageSizeUpdate = true;
      }

      element.carouselInstance?.requestUpdate?.();
    });
  };

  window.addEventListener('scroll', startCascade, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeCarousels());
  } else {
    initializeCarousels();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeCarousels(event.target);
  });

  document.addEventListener('carousel:content-removed', (event) => {
    carouselElements(event.target).forEach((element) => element.carouselInstance?.destroy());
  });

  document.addEventListener('carousel:content-updated', (event) => {
    initializeCarousels(event.target);
    refreshCarousels(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    carouselElements(event.target).forEach((element) => element.carouselInstance?.destroy());
  });
})();
